import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/lib/api/client';
import type { AdPlacement, HouseAd } from '@/lib/services/store.service';
import { imagensDaPosicao } from './rotacao';

/** O link do clique: sempre o caminho da nossa API, que conta e redireciona. */
export function linkDoClique(anuncio: HouseAd, posicao: AdPlacement): string | null {
  if (!/^\/public\/ads\/[0-9a-f-]{36}\/click\?store=[0-9a-f-]{36}$/i.test(anuncio.clickPath)) return null;
  return `${API_BASE_URL}${anuncio.clickPath}&placement=${posicao}`;
}

/**
 * Anúncio próprio: imagem e link, sem script de terceiro nem cookie. Os textos
 * são texto puro. O selo "Anúncio" fica sempre visível (publicidade tem de ser
 * identificável).
 */
export function AnuncioProprio({ anuncio, posicao }: { anuncio: HouseAd; posicao: AdPlacement }) {
  const { t } = useTranslation();
  const href = linkDoClique(anuncio, posicao);
  if (!href) return null;
  const cartao = posicao === 'IN_FEED';
  const { principal, celular } = imagensDaPosicao(anuncio, posicao);
  const temTexto = !!(anuncio.title || anuncio.description || anuncio.cta);

  return (
    <aside aria-label={t('store.ads.label')} data-anuncio={posicao} data-anuncio-id={anuncio.id} className={cartao ? 'h-full' : undefined}>
      <a
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground transition-colors hover:border-primary/50"
      >
        <div className="relative">
          <span className="absolute left-2 top-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {t('store.ads.label')}
          </span>
          {cartao ? (
            <img src={principal ?? ''} alt={anuncio.alt} width={600} height={600} className="block aspect-square h-auto w-full object-cover" />
          ) : (
            <picture>
              {celular && <source media="(max-width: 640px)" srcSet={celular} width={640} height={200} />}
              {/* Proporção fixa da posição: a página não pula quando a imagem carrega, e arquivo torto não vira um bloco gigante. */}
              <img
                src={principal ?? ''}
                alt={anuncio.alt}
                width={1456}
                height={180}
                className={`block h-auto w-full object-cover ${celular ? 'aspect-[640/200] min-[641px]:aspect-[1456/180]' : 'aspect-[1456/180]'}`}
              />
            </picture>
          )}
        </div>
        {temTexto && (
          <div className={cartao ? 'flex flex-1 flex-col gap-2 p-3' : 'flex items-center justify-between gap-3 px-4 py-3'}>
            <div className="min-w-0">
              {anuncio.title && <p className="truncate text-sm font-semibold text-foreground">{anuncio.title}</p>}
              {anuncio.description && <p className="line-clamp-2 text-xs text-muted-foreground">{anuncio.description}</p>}
            </div>
            {anuncio.cta && (
              <span className={`${cartao ? 'mt-auto self-start' : 'shrink-0'} rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground`}>
                {anuncio.cta}
              </span>
            )}
          </div>
        )}
      </a>
    </aside>
  );
}
